import React, { useMemo, useState } from "react";

const FilterSidebar = ({
  filter,
  changeHandler,
  priceChangeHandler,
  configs,
  onClearAll,
  isDrawer = false,
  onCloseDrawer,
  ...props
}) => {
  const [categorySearch, setCategorySearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [openSections, setOpenSections] = useState({
    category: true,
    brand: true,
  });

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const filteredCategories = useMemo(() => {
    const list = configs?.categories || [];
    const seen = new Set();
    return list.filter((cat) => {
      const name = cat?.name?.trim();
      if (!name) return false;
      if (name.toLowerCase() === "all category") return false;
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      const q = categorySearch.trim().toLowerCase();
      if (q && !name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [configs?.categories, categorySearch]);

  const filteredBrands = useMemo(() => {
    const list = configs?.brand || [];
    const q = brandSearch.trim().toLowerCase();
    return q ? list.filter((b) => String(b).toLowerCase().includes(q)) : list;
  }, [configs?.brand, brandSearch]);

  const activeCount =
    (filter?.category ? 1 : 0) +
    (filter?.brand ? 1 : 0) +
    (filter?.minPrice || filter?.maxPrice ? 1 : 0);

  const fakeChange = (name, value) =>
    changeHandler({ target: { name, value } });

  return (
    <aside
      className={`gpfilt-aside ${isDrawer ? "gpfilt-aside-drawer" : "col-lg-3"}`}
    >
      <div className="gpfilt-stack">
        {/* HEADER CARD */}
        <div className="gpfilt-card gpfilt-header-card">
          <div className="gpfilt-header-top">
            <div>
              <div className="gpfilt-eyebrow">Refine</div>
              <h3 className="gpfilt-header-title">
                Filters
                {activeCount > 0 && (
                  <span className="gpfilt-active-badge">{activeCount}</span>
                )}
              </h3>
            </div>
            {isDrawer && (
              <button
                className="gpfilt-drawer-close"
                onClick={onCloseDrawer}
                aria-label="Close filters"
              >
                ×
              </button>
            )}
          </div>
          {activeCount > 0 && (
            <button
              className="gpfilt-clear-all"
              onClick={() => {
                if (onClearAll) onClearAll();
                else {
                  fakeChange("category", "");
                  fakeChange("brand", "");
                  if (priceChangeHandler) priceChangeHandler({});
                }
              }}
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* CATEGORY */}
        <div className="gpfilt-card">
          <button
            className="gpfilt-section-head"
            onClick={() => toggleSection("category")}
            aria-expanded={openSections.category}
          >
            <span>Category</span>
            <svg
              className={`gpfilt-chevron ${
                openSections.category ? "gpfilt-chevron-open" : ""
              }`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {openSections.category && (
            <div className="gpfilt-section-body">
              <div className="gpfilt-search">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="gpfilt-search-icon"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder="Search categories..."
                />
                {categorySearch && (
                  <button
                    className="gpfilt-search-clear"
                    onClick={() => setCategorySearch("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              <ul className="gpfilt-list gpfilt-list-scroll">
                <li>
                  <label
                    className={`gpfilt-radio ${
                      filter?.category === "" ? "gpfilt-radio-active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      checked={filter?.category === ""}
                      value=""
                      onChange={changeHandler}
                      name="category"
                    />
                    <span className="gpfilt-radio-dot" />
                    <span className="gpfilt-radio-label">All Categories</span>
                  </label>
                </li>
                {filteredCategories.map((cat, i) => {
                  const isActive = filter?.category === cat?.name;
                  return (
                    <li key={cat?._id || cat?.name || i}>
                      <label
                        className={`gpfilt-radio ${
                          isActive ? "gpfilt-radio-active" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          checked={isActive}
                          value={cat?.name}
                          onChange={changeHandler}
                          name="category"
                        />
                        <span className="gpfilt-radio-dot" />
                        {cat?.url && (
                          <img
                            className="gpfilt-radio-img"
                            src={cat.url}
                            alt=""
                          />
                        )}
                        <span className="gpfilt-radio-label">{cat?.name}</span>
                      </label>
                    </li>
                  );
                })}
                {filteredCategories.length === 0 && categorySearch && (
                  <li className="gpfilt-empty">
                    No categories match "{categorySearch}"
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* BRAND */}
        {(configs?.brand || []).length > 0 && (
          <div className="gpfilt-card">
            <button
              className="gpfilt-section-head"
              onClick={() => toggleSection("brand")}
              aria-expanded={openSections.brand}
            >
              <span>Brand</span>
              <svg
                className={`gpfilt-chevron ${
                  openSections.brand ? "gpfilt-chevron-open" : ""
                }`}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {openSections.brand && (
              <div className="gpfilt-section-body">
                <div className="gpfilt-search">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="gpfilt-search-icon"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    placeholder="Search brands..."
                  />
                  {brandSearch && (
                    <button
                      className="gpfilt-search-clear"
                      onClick={() => setBrandSearch("")}
                      aria-label="Clear brand search"
                    >
                      ×
                    </button>
                  )}
                </div>

                <ul className="gpfilt-list gpfilt-list-scroll">
                  <li>
                    <label
                      className={`gpfilt-radio ${
                        filter?.brand === "" ? "gpfilt-radio-active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        checked={filter?.brand === ""}
                        value=""
                        onChange={changeHandler}
                        name="brand"
                      />
                      <span className="gpfilt-radio-dot" />
                      <span className="gpfilt-radio-label">All Brands</span>
                    </label>
                  </li>
                  {filteredBrands.map((brand, i) => {
                    const isActive = filter?.brand === brand;
                    return (
                      <li key={i}>
                        <label
                          className={`gpfilt-radio ${
                            isActive ? "gpfilt-radio-active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            checked={isActive}
                            value={brand}
                            onChange={changeHandler}
                            name="brand"
                          />
                          <span className="gpfilt-radio-dot" />
                          <span className="gpfilt-radio-label">{brand}</span>
                        </label>
                      </li>
                    );
                  })}
                  {filteredBrands.length === 0 && brandSearch && (
                    <li className="gpfilt-empty">
                      No brands match "{brandSearch}"
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {isDrawer && (
          <button
            className="gpfilt-apply-mobile"
            onClick={onCloseDrawer}
          >
            View Results
          </button>
        )}
      </div>

      <style>{`
        .gpfilt-aside {
          padding-right: 0;
        }
        .gpfilt-stack {
          display: flex;
          flex-direction: column;
          gap: 12px;
          position: sticky;
          top: 90px;
        }
        .gpfilt-aside-drawer .gpfilt-stack {
          position: static;
        }

        .gpfilt-card {
          background: #15110a;
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .gpfilt-header-card {
          padding: 14px 16px;
          background: linear-gradient(135deg, #1a1208 0%, #15110a 100%);
          border-color: rgba(212, 168, 87, 0.3);
        }
        .gpfilt-header-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
        }
        .gpfilt-eyebrow {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #d4a857;
          text-transform: uppercase;
        }
        .gpfilt-header-title {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          margin: 4px 0 0;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .gpfilt-active-badge {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          font-size: 11px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 999px;
          line-height: 1;
        }
        .gpfilt-clear-all {
          margin-top: 10px;
          width: 100%;
          background: transparent;
          border: 1px dashed rgba(212, 168, 87, 0.6);
          color: #d4a857;
          padding: 7px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: background .2s ease, color .2s ease;
        }
        .gpfilt-clear-all:hover {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          border-style: solid;
        }
        .gpfilt-drawer-close {
          background: transparent;
          border: none;
          font-size: 26px;
          line-height: 1;
          color: #9aa0a8;
          cursor: pointer;
          padding: 0 6px;
        }
        .gpfilt-drawer-close:hover { color: #d4a857; }

        .gpfilt-section-head {
          width: 100%;
          background: transparent;
          border: none;
          padding: 14px 16px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1px;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          text-transform: uppercase;
        }
        .gpfilt-chevron {
          transition: transform .25s ease;
          color: #d4a857;
        }
        .gpfilt-chevron-open { transform: rotate(180deg); }

        .gpfilt-section-body {
          padding: 2px 16px 16px;
        }
        .gpfilt-search {
          position: relative;
          margin-bottom: 10px;
        }
        .gpfilt-search input {
          width: 100%;
          background: #0b0b0c;
          border: 1px solid rgba(212, 168, 87, 0.2);
          border-radius: 8px;
          padding: 8px 30px 8px 32px;
          font-size: 12px;
          color: #fff;
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .gpfilt-search input::placeholder { color: #6b7280; }
        .gpfilt-search input:focus {
          border-color: #d4a857;
          box-shadow: 0 0 0 3px rgba(212, 168, 87, 0.15);
        }
        .gpfilt-search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #9aa0a8;
          pointer-events: none;
        }
        .gpfilt-search-clear {
          position: absolute;
          right: 4px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #9aa0a8;
          font-size: 16px;
          cursor: pointer;
          width: 22px;
          height: 22px;
          line-height: 1;
          border-radius: 6px;
        }
        .gpfilt-search-clear:hover { background: rgba(212, 168, 87, 0.15); color: #d4a857; }

        .gpfilt-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .gpfilt-list-scroll {
          max-height: 280px;
          overflow-y: auto;
          padding-right: 4px;
        }
        .gpfilt-list-scroll::-webkit-scrollbar { width: 5px; }
        .gpfilt-list-scroll::-webkit-scrollbar-thumb {
          background: rgba(212, 168, 87, 0.2);
          border-radius: 999px;
        }
        .gpfilt-list-scroll::-webkit-scrollbar-thumb:hover { background: #d4a857; }

        .gpfilt-radio {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 7px 10px;
          border-radius: 8px;
          cursor: pointer;
          transition: background .15s ease, color .15s ease;
          color: #cfd2d6;
          font-size: 12.5px;
          line-height: 1.3;
        }
        .gpfilt-radio:hover {
          background: rgba(212, 168, 87, 0.08);
          color: #fff;
        }
        .gpfilt-radio input { display: none; }
        .gpfilt-radio-dot {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 2px solid #4b5563;
          flex-shrink: 0;
          position: relative;
          transition: border-color .2s ease;
        }
        .gpfilt-radio-active {
          background: linear-gradient(90deg, rgba(212,168,87,0.18), rgba(212,168,87,0.04));
          color: #d4a857;
          font-weight: 600;
        }
        .gpfilt-radio-active .gpfilt-radio-dot {
          border-color: #d4a857;
        }
        .gpfilt-radio-active .gpfilt-radio-dot::after {
          content: "";
          position: absolute;
          inset: 1px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
        }
        .gpfilt-radio-img {
          width: 20px;
          height: 20px;
          border-radius: 5px;
          object-fit: cover;
          background: rgba(255,255,255,0.06);
          flex-shrink: 0;
        }
        .gpfilt-radio-label {
          flex: 1;
          word-break: break-word;
        }

        .gpfilt-empty {
          color: #6b7280;
          font-size: 12px;
          padding: 8px 10px;
          font-style: italic;
        }

        .gpfilt-apply-mobile {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          border: none;
          padding: 13px 18px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.6px;
          cursor: pointer;
          margin-top: 4px;
        }

        @media (max-width: 991px) {
          .gpfilt-aside.col-lg-3 { display: none; }
        }
      `}</style>
    </aside>
  );
};

export default FilterSidebar;
